<template>
  <d-container fluid class="main-content-container px-4">
    <d-alert
      v-if="isParentProduct && children.length"
      show
      theme="primary"
      class="text-center">
      {{ $t('product.rememberAboutVariants') }}
    </d-alert>
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">
          {{ $t('product.products') }}
        </span>
        <h3 class="page-title">
          <template v-if="isIndentList">
            {{ $t('product.editIndentProduct') }}
          </template>
          <template v-else>
            <template v-if="isParentProduct">
              {{ $t('product.editProduct') }}
            </template>
            <template v-else>
              {{ $t('product.editProductVariant') }}
            </template>
          </template>
        </h3>
      </d-col>
      <d-col
        col
        sm="6"
        class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="secondary"
          @click.prevent="$router.push(backUrl)"
          class="mr-1">
          <i class="material-icons">arrow_back</i>
          {{ $t('ui.backToList') }}
        </d-button>
        <d-button
          v-if="!isIndentList && !isParentProduct"
          theme="secondary"
          :outline="true"
          @click.prevent="$router.push(parentBackUrl)"
          class="mr-1">
          <i class="material-icons">expand_less</i>
          {{ $t('product.backToParentProduct') }}
        </d-button>
        <d-button
          theme="success"
          @click.prevent="validateForm">
          {{ $t('ui.saveChanges') }}
        </d-button>
      </d-col>
    </d-row>

    <!-- Form -->
    <d-form>
      <validation-observer ref="product-edit-form" tag="form">
        <d-card class="mb-5">
          <d-tabs card v-if="!loadError">
            <!-- Dane ogólne -->
            <d-tab
              :title="$t('ui.generalInfo')"
              active>
              <h4 class="mb-3 pb-3 border-bottom">
                {{ $t('ui.generalInfo') }}
              </h4>

              <d-row>
                <d-col cols="6">
                  <div class="form-group">
                    <label>
                      {{ $t('product.productName') }}:
                    </label>

                    <validation-provider
                      rules="required"
                      name="name"
                      v-slot="{ errors }">
                      <d-input
                        :state="errors.length ? 'invalid' : null"
                        v-model="name" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>
                <d-col cols="6">
                  <div class="form-group pt-3">
                    <d-checkbox
                      inline
                      v-model="is_sale"
                      toggle
                      toggle-small
                      class="mt-2 mb-0">
                      {{ $t('product.isSale') }}
                    </d-checkbox>
                    <div style="margin-top: -8px">
                      <small class="note">
                        <template v-if="is_parent">
                          {{ $t('product.saleNoteForParentProduct') }}
                        </template>
                      </small>
                    </div>
                  </div>
                </d-col>
              </d-row>

              <d-row v-if="!isParentProduct">
                <d-col cols="6">
                  <div class="form-group">
                    <label>
                      {{ $t('product.eanGtinCode') }}:
                    </label>

                    <d-input
                      v-model="ean" />
                  </div>
                </d-col>
              </d-row>

              <d-row>
                <d-col cols="4">
                  <div class="form-group">
                    <label>
                      {{ $t('product.minimalQty') }}:
                    </label>

                    <validation-provider
                      rules="required"
                      name="minimum_quantity"
                      v-slot="{ errors }">
                      <d-input-group :append="$t('ui.piecesAbbr')">
                        <d-input
                          type="number"
                          :min="0"
                          :step="1"
                          :state="errors.length ? 'invalid' : null"
                          v-model="minimum_quantity" />
                      </d-input-group>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col cols="4">
                  <div class="form-group">
                    <label>
                      {{ $t('product.piecesInBox') }}:
                    </label>

                    <validation-provider
                      rules="required"
                      name="pieces_in_carton"
                      v-slot="{ errors }">
                      <d-input-group :append="$t('ui.piecesAbbr')">
                        <d-input
                          type="number"
                          :min="0"
                          :step="1"
                          :state="errors.length ? 'invalid' : null"
                          v-model="pieces_in_carton" />
                      </d-input-group>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col cols="4">
                  <div class="form-group">
                    <label>
                      {{ $t('product.actualPiecesInBox') }}:
                    </label>

                    <validation-provider
                      rules="required"
                      name="real_pieces_in_carton"
                      v-slot="{ errors }">
                      <d-input-group :append="$t('ui.piecesAbbr')">
                        <d-input
                          type="number"
                          :min="0"
                          :step="1"
                          :state="errors.length ? 'invalid' : null"
                          v-model="real_pieces_in_carton" />
                      </d-input-group>
                    </validation-provider>
                  </div>
                </d-col>
              </d-row>

              <d-row>
                <d-col cols="6">
                  <div class="form-group">
                    <label>
                      {{ $t('product.productCode') }}:
                    </label>

                    <validation-provider
                      rules="required"
                      name="code"
                      v-slot="{ errors }">
                      <d-input
                        :state="errors.length ? 'invalid' : null"
                        v-model="code" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col cols="6">
                  <div class="form-group">
                    <label>
                      {{ $t('product.prductPriceCode') }}:

                      <a
                        v-if="same_price_code.length > 1"
                        href="#"
                        @click.prevent="allowPriceCodeEdit">
                        <i class="material-icons">edit</i>
                        {{ $t('ui.edit') }}
                      </a>
                    </label>

                    <validation-provider
                      :rules="isIndentList ? '' : 'required'"
                      name="price_code"
                      v-slot="{ errors }">
                      <d-input
                        :state="errors.length ? 'invalid' : null"
                        :disabled="!priceCodeCanBeEdited"
                        v-model="price_code" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>

                    <small v-if="same_price_code.length > 1">
                      {{ $t('product.usedByXOtherProducts', { count: same_price_code.length }) }}
                      <a
                        href="#"
                        @click.prevent="showSamePriceCodeProductsPopup = true">
                        ({{ $t('product.seeSamePriceCodeProducts') }})
                      </a>
                    </small>
                    <small
                      v-if="copyPriceCodeDuringSave"
                      class="text-warning d-block">
                      <i class="material-icons">warning</i> {{ $t('product.priceCodeWillBeUpdatedInOtherProducts') }}
                    </small>
                  </div>
                </d-col>
              </d-row>

              <d-row>
                <d-col cols="2">
                  <div class="form-group">
                    <d-checkbox
                      inline
                      v-model="include_discounts"
                      toggle
                      toggle-small>
                      {{ $t('product.includeDiscounts') }}
                    </d-checkbox>
                  </div>
                </d-col>

                <d-col cols="2">
                  <div class="form-group">
                    <d-checkbox
                      inline
                      v-model="include_in_services"
                      toggle
                      toggle-small>
                      {{ $t('product.includeInServices') }}
                    </d-checkbox>
                  </div>
                </d-col>

                <d-col cols="2">
                  <div class="form-group">
                    <d-checkbox
                      inline
                      v-model="disable_for_customer"
                      toggle
                      toggle-small>
                      {{ $t('product.disableForCustomer') }}
                    </d-checkbox>
                  </div>
                </d-col>

                <d-col cols="2">
                  <div class="form-group">
                    <d-checkbox
                      inline
                      v-model="disable_for_customer_api"
                      toggle
                      toggle-small>
                      {{ $t('product.disableForCustomerAPI') }}
                    </d-checkbox>
                  </div>
                </d-col>

                <d-col cols="2">
                  <div class="form-group">
                    <d-checkbox
                      inline
                      v-model="auto_sample_date"
                      toggle
                      toggle-small>
                      {{ $t('product.automaticallySetSamplesDates') }}
                    </d-checkbox>
                  </div>
                </d-col>

                <d-col cols="2">
                  <div class="form-group">
                    <d-checkbox
                      inline
                      v-model="available_in_purchase"
                      toggle
                      toggle-small>
                      {{ $t('product.availableInPurchases') }}
                    </d-checkbox>
                  </div>
                </d-col>
              </d-row>

              <d-row>
                <d-col
                  v-if="isIndentList"
                  cols="6">
                  <label>
                    {{ $t('product.productIndentBaseProduct') }}:
                  </label>

                  <div class="form-group">
                    <div :class="{ 'form-control': true, 'is-select': true }">
                      <v-select
                        class="w-100"
                        :options="availableProducts"
                        v-model="base_product_id"
                        :reduce="product => product.id"
                        :placeholder="$t('product.chooseProduct')"
                        label="code"
                        :filterBy="filterByForProducts">
                        <template slot="no-options">
                          {{ $t('product.noProductsMatchingGivenPhrase') }}&hellip;
                        </template>
                        <template slot="option" slot-scope="option">
                          <div class="d-center">
                            <template v-if="option.code">{{ option.code}} - </template>{{ option.name }}
                          </div>
                        </template>
                        <template slot="selected-option" slot-scope="option">
                          <div class="selected d-center">
                            <template v-if="option.code">{{ option.code}} - </template>{{ option.name }}
                          </div>
                        </template>
                      </v-select>
                    </div>
                  </div>
                </d-col>

                <d-col :cols="isIndentList ? 6 : 12">
                  <label>
                    {{ $t('product.productDefaultServices') }}:
                  </label>

                  <div class="form-group">
                    <div :class="{ 'form-control': true, 'is-select': true }">
                      <v-select
                        class="w-100"
                        :options="availableServices"
                        v-model="default_services"
                        :reduce="service => service.id"
                        :placeholder="$t('service.chooseService')"
                        label="code"
                        :filterBy="filterByForServices"
                        :multiple="true">
                        <template slot="no-options">
                          {{ $t('service.noServicesMatchingPhrase') }}&hellip;
                        </template>
                        <template slot="option" slot-scope="option">
                          <div class="d-center">
                            <template v-if="option.group_name">{{ option.group_name }} - </template>{{ option.name }}
                          </div>
                        </template>
                        <template slot="selected-option" slot-scope="option">
                          <div class="selected d-center">
                            <template v-if="option.group_name">{{ option.group_name }} - </template>{{ option.name }}
                          </div>
                        </template>
                      </v-select>
                    </div>
                  </div>
                </d-col>
              </d-row>

              <d-row v-if="available_in_purchase && !isIndentList">
                <d-col cols="6">
                  <purchase-select-shipper
                    key="purchase-shipper-field"
                    ref="purchase-shipper"
                    :isSub="false" />
                </d-col>

                <d-col cols="6">
                  <purchase-select-shipper
                    key="purchase-subshipper-field"
                    ref="purchase-subshipper"
                    :isSub="true" />
                </d-col>
              </d-row>

              <d-row>
                <template v-if="isIndentList">
                  <d-col cols="3">
                    <div class="form-group">
                      <label>
                        {{ $t('product.indentProduct.weight') }}:
                      </label>

                      <validation-provider
                        rules="required|min_value:0|doubleVal"
                        name="weight"
                        v-slot="{ errors }">
                        <d-input-group :class="{'is-invalid': errors.length}">
                          <d-input
                            type="text"
                            class="text-right"
                            :state="errors.length ? 'invalid' : null"
                            v-model="weight" />
                          <d-input-group-text slot="append">
                            kg
                          </d-input-group-text>
                        </d-input-group>
                        <div class="invalid-feedback">
                          {{ errors[0] }}
                        </div>
                      </validation-provider>
                    </div>
                  </d-col>
                </template>

                <d-col cols="3">
                  <div class="form-group">
                    <label>
                      {{ $t('product.indentProduct.cnCode') }}:
                    </label>

                    <validation-provider
                      :rules="isIndentList ? 'required' : ''"
                      name="cn_code"
                      v-slot="{ errors }">
                      <d-input
                        :state="errors.length ? 'invalid' : null"
                        v-model="cn_code" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col :cols="isIndentList ? 6 : 9">
                  <div class="form-group">
                    <label>
                      {{ $t('product.linkToWWWPage') }}:
                    </label>

                    <validation-provider
                      rules="valid-url"
                      name="site-url"
                      v-slot="{ errors }">
                      <d-input
                        :state="errors.length ? 'invalid' : null"
                        v-model="site_url" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>
              </d-row>

              <d-row>
                <d-col cols="4">
                  <label class="d-block">
                      {{ $t('product.indentProduct.type') }}:
                  </label>

                  <d-button-group size="small" class="">
                    <d-button
                      theme="primary"
                      :outline="type !== null"
                      @click.prevent="setIndentProductType(null)">
                      {{ $t('product.indentProduct.types.none') }}
                    </d-button>
                    <d-button
                      theme="primary"
                      :outline="type !== 'CAP'"
                      @click.prevent="setIndentProductType('CAP')">
                      {{ $t('product.indentProduct.types.cap') }}
                    </d-button>
                    <d-button
                      theme="primary"
                      :outline="type !== 'WINTER'"
                      @click.prevent="setIndentProductType('WINTER')">
                      {{ $t('product.indentProduct.types.winter') }}
                    </d-button>
                  </d-button-group>
                </d-col>

                <d-col
                  v-if="isIndentList"
                  cols="2">
                  <div class="form-group">
                    <label>
                      {{ $t('product.indentProduct.originCountry') }}:
                    </label>

                    <validation-provider
                      rules="required"
                      name="origin_country"
                      v-slot="{ errors }">
                      <d-input
                        :state="errors.length ? 'invalid' : null"
                        v-model="origin_country" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col :cols="isIndentList ? 6 : 8">
                  <div class="form-group">
                    <label>
                      {{ $t('product.linkToDropbox') }}:
                    </label>

                    <validation-provider
                      rules="valid-url"
                      name="dropbox-url"
                      v-slot="{ errors }">
                      <d-input
                        :state="errors.length ? 'invalid' : null"
                        v-model="dropbox_url" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>
              </d-row>

              <div class="form-group">
                <label>
                  {{ $t('ui.shortDescription') }}:
                </label>

                <validation-provider
                  :rules="isIndentList ? '' : 'required'"
                  name="intro"
                  v-slot="{ errors }">
                  <d-input
                    :state="errors.length ? 'invalid' : null"
                    v-model="intro" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label>
                  {{ $t('product.productDescription') }}:
                </label>

                <validation-provider
                  rules="required"
                  name="description"
                  v-slot="{ errors }">
                  <d-textarea
                    :state="errors.length ? 'invalid' : null"
                    v-model="description"
                    :value.prop="description"
                    :rows="8"></d-textarea>
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <hr />

              <d-row>
                <d-col cols="6">
                  <div class="form-group">
                    <label>
                      {{ $t('product.siteDescription') }}:
                    </label>

                    <d-textarea
                      v-model="site_description"
                      :value.prop="site_description"
                      :rows="8" />
                  </div>
                </d-col>

                <d-col cols="6">
                  <div class="form-group">
                    <label>
                      {{ $t('product.descriptionForCustomer') }}:
                    </label>

                    <d-textarea
                      v-model="description_for_customer"
                      :value.prop="description_for_customer"
                      :rows="8" />
                  </div>
                </d-col>
              </d-row>

              <hr />

              <d-row>
                <d-col cols="6">
                  <div class="form-group">
                    <label>
                      {{ $t('product.productPhoto') }}:
                    </label>

                    <div v-if="image_url">
                      <img :src="image_url" />
                    </div>

                    <div v-if="image_url">
                      <a
                        href="#"
                        @click.prevent="removeProductImage()"
                        class="text-danger">
                        <small>{{ $t('image.removePhoto') }}</small>
                      </a>
                    </div>

                    <file-input
                      v-if="!image_url"
                      :onChange="setProductImage"
                      id="inputFileProductImage" />
                  </div>
                </d-col>

                <d-col cols="6">
                  <div class="form-group">
                    <label>
                      {{ $t('product.visualizationTemplate') }}:
                    </label>

                    <div v-if="visualization_template_url">
                      <a
                        href="#"
                        @click.prevent="downloadFile(visualization_template_url)">
                        <div
                          v-if="downloadingFile"
                          class="spinner-border spinner-border-sm"
                          role="status"></div>
                        <i
                          v-else
                          class="material-icons">cloud_download</i>
                        {{ $t('purchase.downloadFile') }}
                      </a>
                    </div>

                    <div v-if="visualization_template_url">
                      <a
                        href="#"
                        @click.prevent="removeVisualizationTemplate()"
                        class="text-danger">
                        <small>{{ $t('purchase.removeFile') }}</small>
                      </a>
                    </div>

                    <file-input
                      v-if="!visualization_template_url"
                      :onChange="setVisualizationTemplate"
                      id="inputFileVisualizationTemplateImage" />
                  </div>
                </d-col>
              </d-row>
            </d-tab>

            <d-tab
              v-if="!isIndentList"
              :title="$t('image.photoGallery')">
              <vue-dropzone
                ref="vueDropzone"
                id="dropzone"
                :options="dropZoneConfig"
                @vdropzone-complete="loadPhotos"></vue-dropzone>

              <table
                v-if="!loadPhotosError"
                class="table mt-2 mb-0 full-width">
                <thead class="bg-light">
                  <tr>
                    <th scope="col" class="border-0" style="width: 40px;"></th>
                    <th scope="col" class="border-0" style="width: 80px;">
                      <template v-if="!photosOrderSaveInProgress">{{ $t('image.photo') }}</template>
                    </th>
                    <th scope="col" :class="{ 'border-0': true, 'text-center': photosOrderSaveInProgress }" style="width: calc(100% - 270px);">
                      <template v-if="!photosOrderSaveInProgress">{{ $t('product.fileName') }}</template>
                      <template v-else>{{ $t('ui.saving') }}&hellip;</template>
                    </th>
                    <th scope="col" class="border-0" style="width: 150px;">
                      <template v-if="!photosOrderSaveInProgress">{{ $t('ui.actions') }}</template>
                    </th>
                  </tr>
                </thead>
                <draggable
                  v-if="photos.length && loadedPhotos"
                  v-model="photos"
                  tag="tbody">
                  <tr
                    v-for="(photo, index) of photos"
                    :key="'photo-' + index">
                    <td class="align-middle is-draggable">
                      <i class="material-icons">unfold_more</i>
                    </td>
                    <td class="align-middle">
                      <img
                        :src="photo.image_url"
                        height="80"
                        width="80"
                        alt="" />
                    </td>
                    <td class="align-middle">
                      {{ photo.image }}
                    </td>
                    <td class="align-middle">
                      <d-button
                        theme="danger"
                        :disabled="removePhotoInProgress"
                        @click.prevent="removePhoto(photo.id)">
                        <i class="material-icons">delete</i>
                        {{ $t('ui.delete') }}
                      </d-button>
                    </td>
                  </tr>
                </draggable>
                <tbody v-else-if="!photos.length">
                  <tr>
                    <td colspan="4" class="text-center">
                      {{ $t('product.noPhotosForThisProduct') }}&hellip;
                    </td>
                  </tr>
                </tbody>
                <tbody v-else>
                  <tr>
                    <td colspan="4" class="text-center">
                      {{ $t('image.photosLoading') }}&hellip;
                    </td>
                  </tr>
                </tbody>
              </table>

              <d-alert
                v-if="loadPhotosError"
                show
                theme="warning">
                {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
              </d-alert>
            </d-tab>

            <d-tab
              v-if="!isIndentList"
              :title="$t('image.webGallery')">
              <vue-dropzone
                ref="vueDropzone"
                id="dropzone"
                :options="webDropZoneConfig"
                @vdropzone-complete="loadWebPhotos"></vue-dropzone>

              <table
                v-if="!loadWebPhotosError"
                class="table mt-2 mb-0 full-width">
                <thead class="bg-light">
                  <tr>
                    <th scope="col" class="border-0" style="width: 40px;"></th>
                    <th scope="col" class="border-0" style="width: 80px;">
                      <template v-if="!webPhotosOrderSaveInProgress">{{ $t('image.photo') }}</template>
                    </th>
                    <th scope="col" :class="{ 'border-0': true, 'text-center': webPhotosOrderSaveInProgress }" style="width: calc(100% - 270px);">
                      <template v-if="!webPhotosOrderSaveInProgress">{{ $t('product.fileName') }}</template>
                      <template v-else>{{ $t('ui.saving') }}&hellip;</template>
                    </th>
                    <th scope="col" class="border-0" style="width: 150px;">
                      <template v-if="!webPhotosOrderSaveInProgress">{{ $t('ui.actions') }}</template>
                    </th>
                  </tr>
                </thead>
                <draggable
                  v-if="webPhotos.length && loadedWebPhotos"
                  v-model="webPhotos"
                  tag="tbody">
                  <tr
                    v-for="(photo, index) of webPhotos"
                    :key="'photo-' + index">
                    <td class="align-middle is-draggable">
                      <i class="material-icons">unfold_more</i>
                    </td>
                    <td class="align-middle">
                      <img
                        :src="photo.image_url"
                        height="80"
                        width="80"
                        alt="" />
                    </td>
                    <td class="align-middle">
                      {{ photo.image }}
                    </td>
                    <td class="align-middle">
                      <d-button
                        theme="danger"
                        :disabled="removeWebPhotoInProgress"
                        @click.prevent="removeWebPhoto(photo.id)">
                        <i class="material-icons">delete</i>
                        {{ $t('ui.delete') }}
                      </d-button>
                    </td>
                  </tr>
                </draggable>
                <tbody v-else-if="!webPhotos.length">
                  <tr>
                    <td colspan="4" class="text-center">
                      {{ $t('product.noPhotosForThisProduct') }}&hellip;
                    </td>
                  </tr>
                </tbody>
                <tbody v-else>
                  <tr>
                    <td colspan="4" class="text-center">
                      {{ $t('image.photosLoading') }}&hellip;
                    </td>
                  </tr>
                </tbody>
              </table>

              <d-alert
                v-if="loadWebPhotosError"
                show
                theme="warning">
                {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
              </d-alert>
            </d-tab>

            <d-tab :title="$t('price.prices')">
              <div class="form-group">
                <label>
                  {{ $t('product.importantPriceInfo') }}:
                </label>

                <d-textarea
                  v-model="price_message"
                  :value.prop="price_message"
                  :rows="4" />
              </div>

              <h4 class="mb-3 pb-3 border-bottom d-flex">
                {{ $t('product.productPrices') }}

                <d-button
                  v-if="isIndentList"
                  theme="success"
                  @click.prevent="addPrice"
                  class="ml-auto">
                  <i class="material-icons">add</i>
                  {{ $t('price.addPrice') }}
                </d-button>
              </h4>

              <table class="table mt-2 mb-0 full-width">
                <thead class="bg-light">
                  <tr>
                    <th scope="col" class="border-0">{{ $t('price.price') }}</th>
                    <th scope="col" class="border-0">{{ $t('settings.currency.currency') }}</th>
                    <th
                      v-if="isIndentList"
                      scope="col"
                      class="border-0"
                      style="width: 100px">
                      {{ $t('ui.actions') }}
                    </th>
                  </tr>
                </thead>
                <tbody v-if="prices.length">
                  <tr
                    v-for="(price, index) of prices"
                    :key="'price-' + index">
                    <template v-if="isIndentList">
                      <td class="align-middle">
                        <validation-provider
                          rules="required|min_value:0|doubleVal"
                          :name="'product-indent-price-' + index"
                          v-slot="{ errors }">
                          <d-input
                            v-model="price.price"
                            :state="errors.length ? 'invalid' : null" />
                          <div class="invalid-feedback">
                            {{ errors[0] }}
                          </div>
                        </validation-provider>
                      </td>
                      <td>
                        <validation-provider
                          rules="required"
                          :name="'product-indent-currency-' + index"
                          class="w-25 ml-2"
                          v-slot="{ errors }">
                          <d-form-select
                            v-model="price.currency_id"
                            :state="errors.length ? 'invalid' : null"
                            :options="currencyOptions" />
                          <div class="invalid-feedback">
                            {{ errors[0] }}
                          </div>
                        </validation-provider>
                      </td>
                      <td>
                        <d-button
                          theme="danger"
                          @click.native.prevent="removePrice(index)"
                          :title="$t('ui.delete')">
                          <i class="material-icons">delete</i>
                        </d-button>
                      </td>
                    </template>
                    <template v-else>
                      <td class="align-middle">
                        {{ price.price }}
                      </td>
                      <td class="align-middle">
                        <template v-if="price.currency">
                          {{ price.currency.code }}
                        </template>
                      </td>
                    </template>
                  </tr>
                </tbody>
                <tbody v-else>
                  <tr>
                    <td colspan="3" class="text-center">
                      {{ $t('product.noPricesDefinedForThisProduct') }}&hellip;
                    </td>
                  </tr>
                </tbody>
              </table>
            </d-tab>

            <d-tab :title="$t('settings.transaltion.translations')">
              <h4 class="pb-3 mb-0 border-bottom d-flex">
                {{ $t('settings.transaltion.translations') }}

                <d-button
                  class="ml-auto"
                  theme="success"
                  @click.prevent="addTranslation">
                  <i class="material-icons">add</i>
                  {{ $t('settings.transaltion.addTranslation') }}
                </d-button>
              </h4>

              <table class="table mt-2 mb-0 full-width">
                <thead class="bg-light">
                  <tr>
                    <th scope="col" class="border-0" style="width: 150px;">{{ $t('ui.language') }}</th>
                    <th scope="col" class="border-0" style="width: calc(100% - 350px);">{{ $t('product.productName') }}</th>
                    <th scope="col" class="border-0" style="width: 200px;">{{ $t('ui.actions') }}</th>
                  </tr>
                </thead>
                <tbody v-if="product_translations.length">
                  <tr
                    v-for="(translation, index) of product_translations"
                    :key="'price-' + index">
                    <td class="align-middle">
                      {{ getLanguageByID(translation.language_id) }}
                    </td>
                    <td class="align-middle">
                      {{ translation.name }}
                    </td>
                    <td class="align-middle">
                      <d-button
                        theme="primary"
                        @click.prevent="editTranslation(translation)"
                        class="mr-2"
                        :title="$t('ui.edit')">
                        <i class="material-icons">edit</i>
                      </d-button>
                      <d-button
                        theme="danger"
                        :disabled="removeTranslationInProgress"
                        @click.prevent="removeTranslation(translation.id)"
                        :title="$t('ui.delete')">
                        <i class="material-icons">delete</i>
                      </d-button>
                    </td>
                  </tr>
                </tbody>
                <tbody v-else>
                  <tr>
                    <td colspan="3" class="text-center">
                      {{ $t('product.noTranslationsForThisProduct') }}&hellip;
                    </td>
                  </tr>
                </tbody>
              </table>
            </d-tab>

            <d-tab :title="$t('ui.tags')">
              <h4 class="pb-3 mb-0 border-bottom d-flex">
                {{ $t('product.tag.productTags') }}
              </h4>

              <d-alert
                v-if="!tagCategories.length"
                show
                theme="warning">
                {{ $t('product.tag.noAvailableTags') }}&hellip;
              </d-alert>

              <div
                v-for="(tagCategory, index) of tagCategories"
                :key="'tag-category-' + index"
                class="mt-4 tags-category">
                <h6>
                  <span class="text-left">{{ tagCategory.name }}</span>
                </h6>

                <p class="d-flex mt-2 mb-2">
                  <d-input
                    v-model="tagSearch[index]"
                    :placeholder="$t('ui.search')" />
                  <d-button
                    v-if="tagSearch[index]"
                    theme="danger"
                    class="ml-2 w-25"
                    @click.prevent="clearTagSearch(index)">
                    {{ $t('ui.clearFilter') }}
                  </d-button>
                </p>

                <ol v-if="tagCategory.tags && tagCategory.tags.length">
                  <li
                    v-for="(tag, subindex) of filteredTags(tagCategory.tags, index)"
                    :key="'tag-item-' + index + '-' + subindex"
                    :data-tag-id="tag.id"
                    :data-active="tags.indexOf(tag.id) > -1 ? 'true' : 'false'"
                    @click="toggleTag(tag.id)">
                    {{ tag.name }}
                  </li>
                </ol>

                <d-alert
                  v-if="!(tagCategory.tags && tagCategory.tags.length) && !tagSearch[index]"
                  show
                  theme="warning">
                  {{ $t('product.tag.noAvailableTagsForThisCategory') }}&hellip;
                </d-alert>

                <d-alert
                  v-if="tagSearch[index] && !filteredTags(tagCategory.tags, index).length"
                  show
                  theme="warning">
                  {{ $t('product.tag.noMatchingTagsFound') }}
                </d-alert>
              </div>

              IPP {{ isParentProduct }} {{ !isIndentList }}
            </d-tab>

            <d-tab :title="$t('productCategories.categories')">
              <div class="form-group">
                <label>
                  {{ $t('productCategories.mainCategory') }}:
                </label>

                <div :class="{
                  'form-control': true,
                  'is-select': true,
                }">
                  <v-select
                    :options="categoriesStructure"
                    v-model="category_id"
                    :reduce="category => category.id"
                    :placeholder="$t('productCategories.noMainCategory')"
                    label="name"
                    :clearable="true">
                    <template slot="no-options">
                      {{ $t('productCategories.noCategoriesToDisplay') }}&hellip;
                    </template>
                    <template slot="option" slot-scope="option">
                      <div class="d-center" v-pure-html="option.name"></div>
                    </template>
                    <template slot="selected-option" slot-scope="option">
                      <div class="selected d-center" v-pure-html="option.name"></div>
                    </template>
                  </v-select>
                </div>
              </div>

              <div class="form-group">
                <label>
                  {{ $t('productCategories.additionalCategories') }}:
                </label>

                <ul class="additional-categories">
                  <li
                    v-for="(category, index) of categoriesStructure"
                    :key="'additional-category-' + index"
                    :class="{
                      'additional-category': true,
                      'is-disabled': category.id === category_id
                    }">
                    <d-checkbox
                      v-model="additionalCategories[category.id]"
                      toggle
                      toggle-small
                      :disabled="category.id === category_id">
                      <span v-pure-html="category.name"></span>
                    </d-checkbox>
                  </li>
                </ul>
              </div>
            </d-tab>

            <d-tab
              v-if="!isIndentList"
              :disabled="!isParentProduct"
              :title="$t('product.variants')">
              <h4 class="pb-3 mb-0 d-flex">
                <span>{{ $t('product.variants') }}</span>

                <d-button
                  theme="success"
                  class="ml-auto"
                  @click.prevent="showAddVariantPopup = true;">
                  {{ $t('product.addVariant') }}
                </d-button>
              </h4>

              <product-variants
                v-if="allDataLoaded"
                :products="children"
                :currencies="currencies" />
            </d-tab>

            <d-tab
              v-if="!isIndentList"
              :disabled="!canCopyToVariants || !isParentProduct"
              :title="$t('product.copyToVariants')">
              <h4 class="pb-3 mb-0 border-bottom d-flex">
                {{ $t('product.copyToVariantsTitle') }}

                <d-button
                  class="ml-auto"
                  theme="success"
                  @click.prevent="copyToVariants">
                  <i class="material-icons">file_copy</i>
                  {{ $t('product.copyToVariants2') }}
                </d-button>
              </h4>

              <d-alert
                show
                theme="danger"
                class="mt-5 mb-5">
                {{ $t('product.variantValuesWillBeReplacedDuringCopyOperation')  }}
              </d-alert>

              <h5 class="mt-4 mb-4 d-flex">
                <span>
                  {{ $t('product.productVariantsToModify') }}
                </span>

                <small class="d-flex ml-auto">
                  <d-checkbox
                    toggle
                    toggle-small
                    v-model="listOfChildrensToCopyCheckAll">
                    {{ $t('ui.selectAll') }}
                  </d-checkbox>
                </small>
              </h5>

              <table class="table">
                <thead>
                  <tr>
                    <th>{{ $t('ui.name') }}</th>
                    <th>{{ $t('product.productCode') }}</th>
                    <th>{{ $t('product.prductPriceCode') }}</th>
                    <th style="width: 140px;">{{ $t('product.shouldBeModified') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(children, index) of children_to_copy"
                    :key="'children-to-copy-' + index">
                    <td>{{ children.name }}</td>
                    <td>{{ children.code }}</td>
                    <td>{{ children.price_code }}</td>
                    <td>
                      <d-checkbox
                        toggle
                        v-model="listOfChildrensToCopy[children.id]" />
                    </td>
                  </tr>
                </tbody>
              </table>

              <hr>

              <h5 class="mt-4 mb-4 d-flex">
                <span>
                  {{ $t('product.valuesToUseInVariants') }}
                </span>

                <small class="d-flex ml-auto">
                  <d-checkbox
                    toggle
                    toggle-small
                    v-model="valuesOfProductToCopyCheckAll">
                    {{ $t('ui.selectAll') }}
                  </d-checkbox>
                </small>
              </h5>

              <table class="table">
                <thead>
                  <tr>
                    <th>{{ $t('product.fieldName') }}</th>
                    <th style="width: 140px;">{{ $t('product.shouldBeCopied') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{{ $t('product.productName') }}</td>
                    <td><d-checkbox toggle v-model="valuesOfProductToCopy['name']" /></td>
                  </tr>
                  <tr>
                    <td>{{ $t('product.prductPriceCode') }}</td>
                    <td><d-checkbox toggle v-model="valuesOfProductToCopy['price_code']" /></td>
                  </tr>
                  <tr>
                    <td>{{ $t('product.automaticallySetSamplesDates') }}</td>
                    <td><d-checkbox toggle v-model="valuesOfProductToCopy['auto_sample_date']" /></td>
                  </tr>
                  <tr>
                    <td>{{ $t('product.includeDiscounts') }}</td>
                    <td><d-checkbox toggle v-model="valuesOfProductToCopy['include_discounts']" /></td>
                  </tr>
                  <tr>
                    <td>{{ $t('product.includeInServices') }}</td>
                    <td><d-checkbox toggle v-model="valuesOfProductToCopy['include_in_services']" /></td>
                  </tr>
                  <tr>
                    <td>{{ $t('product.disableForCustomer') }}</td>
                    <td><d-checkbox toggle v-model="valuesOfProductToCopy['disable_for_customer']" /></td>
                  </tr>
                  <tr>
                    <td>{{ $t('product.disableForCustomerAPI') }}</td>
                    <td><d-checkbox toggle v-model="valuesOfProductToCopy['disable_for_customer_api']" /></td>
                  </tr>
                  <tr>
                    <td>{{ $t('product.availableInPurchases') }}</td>
                    <td><d-checkbox toggle v-model="valuesOfProductToCopy['available_in_purchase']" /></td>
                  </tr>
                  <tr>
                    <td>{{ $t('product.indentProduct.cnCode') }}</td>
                    <td><d-checkbox toggle v-model="valuesOfProductToCopy['cn_code']" /></td>
                  </tr>
                  <tr>
                    <td>{{ $t('product.linkToWWWPage') }}</td>
                    <td><d-checkbox toggle v-model="valuesOfProductToCopy['site_url']" /></td>
                  </tr>
                  <tr>
                    <td>{{ $t('product.linkToDropbox') }}</td>
                    <td><d-checkbox toggle v-model="valuesOfProductToCopy['dropbox_url']" /></td>
                  </tr>
                  <tr>
                    <td>{{ $t('product.productPhoto') }}</td>
                    <td><d-checkbox toggle v-model="valuesOfProductToCopy['image']" /></td>
                  </tr>
                  <tr>
                    <td>{{ $t('product.visualizationTemplate') }}</td>
                    <td><d-checkbox toggle v-model="valuesOfProductToCopy['visualization_template']" /></td>
                  </tr>
                  <tr>
                    <td>{{ $t('ui.shortDescription') }}</td>
                    <td><d-checkbox toggle v-model="valuesOfProductToCopy['intro']" /></td>
                  </tr>
                  <tr>
                    <td>{{ $t('product.productDescription') }}</td>
                    <td><d-checkbox toggle v-model="valuesOfProductToCopy['description']" /></td>
                  </tr>
                  <tr>
                    <td>{{ $t('product.siteDescription') }}</td>
                    <td><d-checkbox toggle v-model="valuesOfProductToCopy['site_description']" /></td>
                  </tr>
                  <tr>
                    <td>{{ $t('product.descriptionForCustomer') }}</td>
                    <td><d-checkbox toggle v-model="valuesOfProductToCopy['description_for_customer']" /></td>
                  </tr>
                  <tr>
                    <td>{{ $t('product.minimalQty') }}</td>
                    <td><d-checkbox toggle v-model="valuesOfProductToCopy['minimum_quantity']" /></td>
                  </tr>
                  <tr>
                    <td>{{ $t('product.piecesInBox') }}</td>
                    <td><d-checkbox toggle v-model="valuesOfProductToCopy['pieces_in_carton']" /></td>
                  </tr>
                  <tr>
                    <td>{{ $t('product.actualPiecesInBox') }}</td>
                    <td><d-checkbox toggle v-model="valuesOfProductToCopy['real_pieces_in_carton']" /></td>
                  </tr>
                  <tr>
                    <td>{{ $t('product.productPrices') }}</td>
                    <td><d-checkbox toggle v-model="valuesOfProductToCopy['prices']" /></td>
                  </tr>
                  <tr>
                    <td>{{ $t('product.importantPriceInfo') }}</td>
                    <td><d-checkbox toggle v-model="valuesOfProductToCopy['price_message']" /></td>
                  </tr>
                  <tr>
                    <td>{{ $t('product.productImagesGallery') }}</td>
                    <td><d-checkbox toggle v-model="valuesOfProductToCopy['images']" /></td>
                  </tr>
                  <tr>
                    <td>{{ $t('image.webGallery') }}</td>
                    <td><d-checkbox toggle v-model="valuesOfProductToCopy['web_images']" /></td>
                  </tr>
                  <tr>
                    <td>{{ $t('product.productTranslations') }}</td>
                    <td><d-checkbox toggle v-model="valuesOfProductToCopy['translations']" /></td>
                  </tr>
                  <tr>
                    <td>{{ $t('product.productTags') }}</td>
                    <td><d-checkbox toggle v-model="valuesOfProductToCopy['tags']" /></td>
                  </tr>
                </tbody>
              </table>
            </d-tab>

            <d-tab :title="$t('product.stateHistory.tabTitle')">
              <h4 class="pb-3 mb-0 border-bottom d-flex">
                {{ $t('product.stateHistory.stateHistoryTitle') }}
              </h4>

              <product-state-history
                :productID="id" />
            </d-tab>
          </d-tabs>

          <div v-if="!allDataLoaded" class="pb-3 pt-3 text-center overlay-loading">
            {{ $t('ui.dataLoading') }}&hellip;
          </div>

          <d-alert
            v-if="loadError"
            show
            theme="warning">
            {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
          </d-alert>
        </d-card>
      </validation-observer>
    </d-form>

    <product-variant-new-popup
      v-if="showAddVariantPopup"
      :parentProductID="id"
      :parentProductName="name"
      :parentProductCode="code"
      :parentProductPriceCode="price_code"
      :parentProductVariants="children" />

    <product-translation-new-popup
      v-if="showAddTranslationPopup"
      :productID="this.id"
      :languages="languages" />

    <product-translation-edit-popup
      v-if="showEditTranslationPopup"
      :productID="this.id"
      :translationData="selectedTranslation"
      :languages="languages" />

    <product-same-price-products-popup
      v-if="showSamePriceCodeProductsPopup"
      :samePriceCode="same_price_code" />
  </d-container>
</template>

<script>
import debounce from 'lodash.debounce';
import vueDraggable from 'vuedraggable';
import vue2Dropzone from 'vue2-dropzone';
import 'vue2-dropzone/dist/vue2Dropzone.min.css';
import CommonGetters from './../../utils/CommonGetters.js';
import FormUtils from './../../utils/FormUtils.js';
import ListUtils from './../../utils/ListUtils.js';
import ProductSamePriceProductsPopup from './ProductSamePriceProductsPopup';
import ProductStateHistory from './ProductStateHistory';
import ProductTranslationNewPopup from './ProductTranslationNewPopup';
import ProductTranslationEditPopup from './ProductTranslationEditPopup';
import ProductVariantNewPopup from './ProductVariantNewPopup';
import ProductVariants from './ProductVariants';
import PurchaseSelectShipper from './../purchases/PurchaseSelectShipper.vue';
import SanitizeUtils from './../../utils/SanitizeUtils';
import Vue from 'vue';

export default {
  name: 'product-edit',
  components: {
    'vue-dropzone': vue2Dropzone,
    'draggable': vueDraggable,
    'product-same-price-products-popup': ProductSamePriceProductsPopup,
    'product-state-history': ProductStateHistory,
    'product-translation-new-popup': ProductTranslationNewPopup,
    'product-translation-edit-popup': ProductTranslationEditPopup,
    'product-variant-new-popup': ProductVariantNewPopup,
    'product-variants': ProductVariants,
    'purchase-select-shipper': PurchaseSelectShipper
  },
  computed: {
    allDataLoaded () {
      return this.loadedData && this.loadedServices && this.loadedProducts && this.loadedPrices && this.loadedLanguages && this.loadedTags && this.loadedCurrencies && this.loadedCategories;
    },
    filteredLanguages () {
      return this.languages.filter(language => language.id !== 1);
    },
    dropZoneConfig () {
      return {
        url: this.$http.defaults.baseURL + 'products/images/store',
        thumbnailWidth: 150,
        thumbnailHeight: 150,
        maxFilesize: 2,
        parallelUploads: 1,
        headers: { 'Authorization': 'Bearer ' + SanitizeUtils.getToken() },
        paramName: 'image',
        dictDefaultMessage: this.$t('image.dropPhotosHere'),
        params: {
          product_id: this.id
        }
      };
    },
    webDropZoneConfig () {
      return {
        url: this.$http.defaults.baseURL + 'products/web-images/store',
        thumbnailWidth: 150,
        thumbnailHeight: 150,
        maxFilesize: 2,
        parallelUploads: 1,
        headers: { 'Authorization': 'Bearer ' + SanitizeUtils.getToken() },
        paramName: 'image',
        dictDefaultMessage: this.$t('image.dropPhotosHere'),
        params: {
          product_id: this.id
        }
      };
    },
    canCopyToVariants () {
      return this.isParentProduct && this.children_to_copy.length > 0;
    },
    backUrl () {
      if (this.isIndentList) {
        return '/produkty-indent';
      }

      return '/produkty';
    },
    parentBackUrl () {
      if (this.isIndentList) {
        return '/produkty-indent/edycja/' + this.parent_id;
      }

      return '/produkty/edycja/' + this.parent_id;
    },
    isParentProduct () {
      return this.parent_id === 0;
    },
    isIndentList () {
      return this.$route.path.indexOf('/produkty-indent') > -1;
    },
    currencyOptions () {
      return [{
        text: this.$t('shipments.popups.selectCurrency'),
        value: null
      }].concat(CommonGetters.getCurrenciesForSelect(this.currencies));
    },
    priceCodeCanBeEdited () {
      if (this.same_price_code.length < 2) {
        return true;
      }

      if (this.same_price_code.length > 1 && this.priceCodeEditAllowed) {
        return true;
      }

      return false;
    },
    categoriesStructure () {
      let result = [];
      let stack = this.categoriesList.map(category => ({ ...category, prefix: '' }));

      while (stack.length > 0) {
        let current = stack.shift();

        result.push({
          id: current.id,
          name: current.prefix + current.name
        });

        if (current.children && current.children.length > 0) {
          stack = [
            ...current.children.map(child => ({ ...child, prefix: current.prefix + '<span style="color: #aaa;">&ndash;</span> ' })),
            ...stack
          ];
        }
      }

      return result;
    }
  },
  watch: {
    photos () {
      this.debouncedPhotosOrdering();
    },
    webPhotos () {
      this.debouncedWebPhotosOrdering();
    },
    listOfChildrensToCopyCheckAll (newValue) {
      let keys = this.children_to_copy.map(children => children.id);

      if (newValue) {
        for (let i = 0; i < keys.length; i++) {
          Vue.set(this.listOfChildrensToCopy, keys[i], true);
        }
      } else {
        for (let i = 0; i < keys.length; i++) {
          Vue.set(this.listOfChildrensToCopy, keys[i], false);
        }
      }
    },
    valuesOfProductToCopyCheckAll (newValue) {
      let keys = Object.keys(this.valuesOfProductToCopy);

      if (newValue) {
        for (let i = 0; i < keys.length; i++) {
          Vue.set(this.valuesOfProductToCopy, keys[i], true);
        }
      } else {
        for (let i = 0; i < keys.length; i++) {
          Vue.set(this.valuesOfProductToCopy, keys[i], false);
        }
      }
    }
  },
  data() {
    return {
      id: 0,
      name: '',
      code: '',
      ean: '',
      price_code: '',
      auto_sample_date: false,
      include_discounts: true,
      include_in_services: true,
      available_in_purchase: false,
      site_url: '',
      dropbox_url: '',
      disable_for_customer: false,
      disable_for_customer_api: false,
      site_description: '',
      description_for_customer: '',
      image: '',
      image_url: '',
      visualization_template: '',
      visualization_template_url: '',
      intro: '',
      parent_id: false,
      is_sale: false,
      is_parent: false,
      description: '',
      minimum_quantity: 0,
      pieces_in_carton: 1,
      real_pieces_in_carton: 1,
      children: [],
      prices: [],
      price_message: '',
      photos: [],
      webPhotos: [],
      same_price_code: [],
      tags: [],
      tagSearch: [],
      tagCategories: [],
      languages: [],
      product_translations: [],
      children_to_copy: [],
      additionalCategories: {},
      category_id: null,
      loadedData: false,
      loadError: false,
      loadPhotosError: false,
      loadWebPhotosError: false,
      loadedPrices: false,
      loadedPhotos: false,
      loadedWebPhotos: false,
      loadedLanguages: false,
      loadedTags: false,
      loadedCurrencies: false,
      photosOrderSaveInProgress: false,
      webPhotosOrderSaveInProgress: false,
      showAddTranslationPopup: false,
      showEditTranslationPopup: false,
      showAddVariantPopup: false,
      selectedTranslation: 0,
      removePhotoInProgress: false,
      removeWebPhotoInProgress: false,
      removeTranslationInProgress: false,
      loadedServices: false,
      loadedProducts: false,
      availableServices: [],
      availableProducts: [],
      default_services: null,
      downloadingFile: false,
      cn_code: '',
      // indent products
      base_product_id: null,
      weight: '0.00',
      origin_country: '',
      currencies: [],
      type: null,
      // internals
      categoriesList: [],
      loadedCategories: false,
      showSamePriceCodeProductsPopup: false,
      copyPriceCodeDuringSave: false,
      priceCodeEditAllowed: false,
      listOfChildrensToCopy: {},
      listOfChildrensToCopyCheckAll: false,
      valuesOfProductToCopyCheckAll: false,
      valuesOfProductToCopy: {
        name: false,
        price_code: false,
        auto_sample_date: false,
        include_discounts: false,
        include_in_services: false,
        disable_for_customer: false,
        disable_for_customer_api: false,
        available_in_purchase: false,
        site_url: false,
        dropbox_url: false,
        image: false,
        visualization_template: false,
        intro: false,
        description: false,
        site_description: false,
        description_for_customer: false,
        minimum_quantity: false,
        pieces_in_carton: false,
        real_pieces_in_carton: false,
        prices: false,
        price_message: false,
        images: false,
        web_images: false,
        translations: false,
        tags: false,
        cn_code: false
      },
      // shippers
      purchase_customer_id: null,
      purchase_sub_customer_id: null
    };
  },
  beforeMount () {
    this.id = parseInt(this.$route.params.id, 10);
  },
  mounted () {
    let self = this;
    let additionalProductRelationsToLoad = ['productTranslations', 'tags', 'defaultServices', 'children', 'children.productPrices'];

    if (this.isIndentList) {
      additionalProductRelationsToLoad = ['productTranslations', 'tags', 'productPrices', 'defaultServices', 'children', 'children.productPrices'];
    }

    FormUtils.loadItemData(this, {
      method: 'get',
      endpointBase: '/products/item/',
      instanceFields: ['name', 'code', 'price_code', 'site_url', 'dropbox_url', 'intro', 'description', 'image', 'image_url', 'product_translations', 'tags', 'minimum_quantity', 'pieces_in_carton', 'real_pieces_in_carton', 'default_services', 'children_to_copy', 'base_product_id', 'cn_code', 'weight', 'origin_country', 'type', 'parent_id', 'children', 'same_price_code', 'ean', 'site_description', 'description_for_customer', 'visualization_template', 'visualization_template_url', 'category_id', 'categories', 'price_message', 'purchase_customer_id', 'purchase_sub_customer_id'],
      loadedKey: 'loadedData',
      errorKey: 'loadError',
      params: {
        with: additionalProductRelationsToLoad
      },
      successAction (response) {
        self.include_discounts = !!response.include_discounts;
        self.include_in_services = !!response.include_in_services;
        self.auto_sample_date = !!response.auto_sample_date;
        self.available_in_purchase = !!response.available_in_purchase;
        self.is_sale = !!response.is_sale;
        self.is_parent = !!response.is_parent;
        self.tags = response.tags.map(tag => tag.id);
        self.additionalCategories = response.categories.reduce((result, category) => { result[category.id] = true; return result; }, {});
        self.disable_for_customer = !!response.disable_for_customer;
        self.disable_for_customer_api = !!response.disable_for_customer_api;

        if (self.isIndentList) {
          self.prices = response.prices;
        }

        Vue.nextTick(() => {
          if (self.$refs['purchase-shipper']) {
            self.$refs['purchase-shipper'].loadShipper(self.purchase_customer_id);
            self.$refs['purchase-subshipper'].loadShipper(self.purchase_sub_customer_id);
          }
        });
      }
    });

    this.loadServices();
    this.loadCategories();

    if (this.isIndentList) {
      this.loadProducts();
      this.loadCurrencies();
      this.loadedPrices = true;
      this.loadedPhotos = true;
    } else {
      this.loadPhotos();
      this.loadWebPhotos();
      this.loadPrices();
      this.loadedCurrencies = true;
      this.loadedProducts = true;
    }

    this.loadLanguages();
    this.loadTags();
    this.$bus.$on('product-translation-new-popup-close', this.closeAddTranslationPopup);
    this.$bus.$on('product-translation-edit-popup-close', this.closeEditTranslationPopup);
    this.$bus.$on('product-reload-translations', this.loadTranslations);
    this.$bus.$on('product-edit-variants-reload-needed', this.reloadVariants);
    this.$bus.$on('product-variant-new-popup-close', this.closeAddVariantPopup);
    this.$bus.$on('product-same-price-code-popup-close', this.closeSamePricePopup);
  },
  methods: {
    validateForm () {
      FormUtils.validate(this.$refs['product-edit-form'], this.submitForm);
    },
    submitForm () {
      let imageFields = false;
      let formDataToSend = {
        is_indent: this.isIndentList ? 1 : 0,
        name: this.name,
        code: this.code,
        price_code: this.price_code,
        auto_sample_date: this.auto_sample_date,
        include_discounts: this.include_discounts,
        disable_for_customer: this.disable_for_customer,
        disable_for_customer_api: this.disable_for_customer_api,
        include_in_services: this.include_in_services,
        available_in_purchase: this.available_in_purchase,
        site_url: this.site_url,
        dropbox_url: this.dropbox_url,
        intro: this.intro,
        is_sale: this.is_sale,
        description: this.description,
        tags: this.tags,
        minimum_quantity: this.minimum_quantity,
        pieces_in_carton: this.pieces_in_carton,
        real_pieces_in_carton: this.real_pieces_in_carton,
        default_services: this.default_services,
        type: this.type === null ? '' : this.type,
        site_description: this.site_description,
        description_for_customer: this.description_for_customer,
        category_id: this.category_id,
        categories: Object.keys(this.additionalCategories).filter(key => this.additionalCategories[key] && key !== this.category_id),
        price_message: this.price_message,
        cn_code: this.cn_code
      };

      if (this.image) {
        formDataToSend.image = this.image;
        imageFields = ['image'];
      } else {
        formDataToSend.image = null;
      }

      if (this.available_in_purchase && !this.isIndentList) {
        formDataToSend.purchase_customer_id = this.$refs['purchase-shipper'].customer_id;
        formDataToSend.purchase_sub_customer_id = this.$refs['purchase-subshipper'].customer_id;
      } else {
        formDataToSend.purchase_customer_id = null;
        formDataToSend.purchase_sub_customer_id = null;
      }

      if (this.visualization_template) {
        formDataToSend.visualization_template = this.visualization_template;

        if (imageFields) {
          imageFields.push('visualization_template');
        } else {
          imageFields = ['visualization_template'];
        }
      } else {
        formDataToSend.visualization_template = null
      }

      if (this.isIndentList) {
        formDataToSend.base_product_id = this.base_product_id;
        formDataToSend.weight = Number(this.weight).toFixed(2);
        formDataToSend.origin_country = this.origin_country;
        formDataToSend.prices = this.prices.map(priceData => ({
          currency_id: priceData.currency_id,
          price: priceData.price
        }));
      }

      if (this.copyPriceCodeDuringSave) {
        formDataToSend.copy_price_code = true;
      }

      if (!this.isParentProduct) {
        formDataToSend.ean = this.ean;
      }

      FormUtils.submit({
        $http: this.$http,
        endpoint: '/products/update/' + this.id,
        formData: formDataToSend,
        successTitle: this.$t('product.editProductSuccessTitle'),
        successText: this.$t('product.editProductSuccessText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('product.editProductErrorText'),
        successAction: () => {
          FormUtils.loadItemData(this, {
            method: 'get',
            endpointBase: '/products/item/',
            instanceFields: ['image', 'image_url'],
            loadedKey: 'loadedData',
            errorKey: 'loadError'
          });
          this.loadPrices();
        }
      }, imageFields);
    },
    loadProducts () {
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/products/all-items',
        loadedKey: 'loadedProducts',
        outputKey: 'availableProducts',
        errorKey: 'loadError',
        params: {
          where: {
            is_indent: 0
          }
        }
      });
    },
    loadServices () {
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/services/all-items',
        loadedKey: 'loadedServices',
        outputKey: 'availableServices',
        errorKey: 'loadError'
      });
    },
    loadPrices () {
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/products/prices/items',
        loadedKey: 'loadedPrices',
        outputKey: 'prices',
        errorKey: 'loadError',
        params: {
          where: {
            product_id: this.id
          }
        }
      });
    },
    loadLanguages () {
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/languages/items',
        loadedKey: 'loadedLanguages',
        outputKey: 'languages',
        errorKey: 'loadError'
      });
    },
    loadPhotos () {
      this.loadedPhotos = false;

      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/products/images/items',
        loadedKey: 'loadedPhotos',
        outputKey: 'photos',
        errorKey: 'loadError',
        params: {
          product_id: this.id
        }
      });
    },
    loadWebPhotos () {
      this.loadedWebPhotos = false;

      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/products/web-images/items',
        loadedKey: 'loadedWebPhotos',
        outputKey: 'webPhotos',
        errorKey: 'loadError',
        params: {
          product_id: this.id
        }
      });
    },
    loadTags () {
      this.loadedTags = false;

      FormUtils.loadAdditionalData(this, {
        endpoint: '/tags/categories/items',
        outputKey: 'tagCategories',
        loadedKey: 'loadedTags',
        params: {
          with: ['tags']
        }
      });
    },
    loadCategories () {
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/categories/items',
        loadedKey: 'loadedCategories',
        outputKey: 'categoriesList',
        errorKey: 'loadError'
      });
    },
    loadCurrencies () {
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/currencies/items',
        loadedKey: 'loadedCurrencies',
        outputKey: 'currencies',
        errorKey: 'loadError'
      });
    },
    removePhoto (photoID) {
      this.removePhotoInProgress = true;

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: '/products/images/delete/',
        id: photoID,
        successTitle: this.$t('image.removePhotoSuccessTitle'),
        successText: this.$t('image.removePhotoSuccessText'),
        confirmTitle: this.$t('image.removePhotoConfirmTitle'),
        confirmText: this.$t('ui.deleteConfirmText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('image.removePhotoErrorText'),
        successAction: () => {
          this.removePhotoInProgress = false;
          this.loadPhotos();
        }
      });
    },
    removeWebPhoto (photoID) {
      this.removeWebPhotoInProgress = true;

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: '/products/web-images/delete/',
        id: photoID,
        successTitle: this.$t('image.removePhotoSuccessTitle'),
        successText: this.$t('image.removePhotoSuccessText'),
        confirmTitle: this.$t('image.removePhotoConfirmTitle'),
        confirmText: this.$t('ui.deleteConfirmText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('image.removePhotoErrorText'),
        successAction: () => {
          this.removeWebPhotoInProgress = false;
          this.loadWebPhotos();
        }
      });
    },
    setProductImage (event) {
      this.image = event.target.files[0];
    },
    setVisualizationTemplate (event) {
      this.visualization_template = event.target.files[0];
    },
    removeProductImage () {
      this.image_url = '';
      this.image = '';
    },
    removeVisualizationTemplate () {
      this.visualization_template_url = '';
      this.visualization_template = '';
    },
    // eslint-disable-next-line
    debouncedPhotosOrdering: debounce(function () {
      this.savePhotosOrder();
    }, 1000),
    savePhotosOrder () {
      this.photosOrderSaveInProgress = true;

      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/products/images/ordering/' + this.id,
        formData: this.photos.map(photo => photo.id),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('image.photosOrderChangeErrorText'),
        successAction: () => {
          this.photosOrderSaveInProgress = false;
        }
      });
    },
    // eslint-disable-next-line
    debouncedWebPhotosOrdering: debounce(function () {
      this.saveWebPhotosOrder();
    }, 1000),
    saveWebPhotosOrder () {
      this.webPhotosOrderSaveInProgress = true;

      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/products/web-images/ordering/' + this.id,
        formData: this.webPhotos.map(photo => photo.id),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('image.photosOrderChangeErrorText'),
        successAction: () => {
          this.webPhotosOrderSaveInProgress = false;
        }
      });
    },
    getLanguageByID (languageID) {
      let filteredLanguages = this.languages.filter(language => language.id === languageID);

      if (filteredLanguages.length) {
        return filteredLanguages[0].name;
      }

      return '';
    },
    loadTranslations () {
      FormUtils.loadItemData(this, {
        method: 'get',
        endpointBase: '/products/item/',
        instanceFields: ['product_translations'],
        loadedKey: null,
        params: {
          with: ['productTranslations']
        }
      });
    },
    addTranslation () {
      this.showAddTranslationPopup = true;
    },
    editTranslation (translationData) {
      this.selectedTranslation = JSON.parse(JSON.stringify(translationData));
      this.showEditTranslationPopup = true;
    },
    closeAddTranslationPopup () {
      this.showAddTranslationPopup = false;
    },
    closeEditTranslationPopup () {
      this.showEditTranslationPopup = false;
    },
    removeTranslation (translationID) {
      this.removeTranslationInProgress = true;

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: '/products/translations/delete/',
        id: translationID,
        successTitle: this.$t('settings.transaltion.deleteTranslationSuccessTitle'),
        successText: this.$t('settings.transaltion.deleteTranslationSuccessText'),
        confirmTitle: this.$t('settings.transaltion.deleteTranslationConfirmTitle'),
        confirmText: this.$t('ui.deleteConfirmText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('settings.transaltion.deleteTranslationErrorText'),
        successAction: () => {
          this.removeTranslationInProgress = false;
          this.loadTranslations();
        },
        cancelAction: () => {
          this.removeTranslationInProgress = false;
        }
      });
    },
    toggleTag (tagID) {
      if (this.tags.indexOf(tagID) > -1) {
        this.tags = this.tags.filter(tag => tag !== tagID).slice();
      } else {
        this.tags.push(tagID);
      }
    },
    filteredTags (tags, index) {
      if (!this.tagSearch[index]) {
        return tags;
      }

      let searchPhrase = this.tagSearch[index].toLocaleLowerCase();
      return tags.filter(tag => tag.name.toLocaleLowerCase().indexOf(searchPhrase) > -1);
    },
    clearTagSearch (index) {
      Vue.set(this.tagSearch, index, '');
    },
    filterByForServices (option, label, search) {
      return (option.code + ' ' + option.group_name + ' ' + option.name).toLowerCase().indexOf(search.toLowerCase()) > -1;
    },
    filterByForProducts (option, label, search) {
      return (option.code + ' ' + option.price_code + ' ' + option.name).toLowerCase().indexOf(search.toLowerCase()) > -1;
    },
    copyToVariants () {
      let childrenIDs = [];
      let fieldsToCopy = [];
      let childrenIDsList = Object.keys(this.listOfChildrensToCopy);
      let fieldNamesList = Object.keys(this.valuesOfProductToCopy);

      for (let i = 0; i < childrenIDsList.length; i++) {
        let id = childrenIDsList[i];

        if (this.listOfChildrensToCopy[id]) {
          childrenIDs.push(parseInt(id, 10));
        }
      }

      for (let i = 0; i < fieldNamesList.length; i++) {
        let name = fieldNamesList[i];

        if (this.valuesOfProductToCopy[name]) {
          fieldsToCopy.push(name);
        }
      }

      if (!childrenIDs.length || !fieldsToCopy.length) {
        Vue.swal({
          title: this.$t('product.cannotCopyToVariantsTitle'),
          html: this.$t('product.cannotCopyToVariantsDesc'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });

        return;
      }

      Vue.swal({
        title: this.$t('product.doYouReallyWantCopyDataToVariantsTitle'),
        text: this.$t('product.doYouReallyWantCopyDataToVariantsDesc'),
        type: 'question',
        showCancelButton: true,
        confirmButtonText: this.$t('product.yesCopyData'),
        cancelButtonText: this.$t('ui.cancel')
      }).then((result) => {
        if (result.value) {
          FormUtils.submitInBackground({
            $http: this.$http,
            endpoint: '/products/copy-to-children',
            errorTitle: this.$t('ui.errorOccured'),
            errorText: this.$t('product.copyToVariantsFailed'),
            formData: {
              copy_from_id: this.id,
              children: childrenIDs,
              fields: fieldsToCopy
            },
            successAction: () => {
              Vue.swal({
                title: this.$t('product.dataCopiedTitle'),
                text: this.$t('product.dataCopiedDesc'),
                type: 'success',
                confirmButtonText: this.$t('ui.ok'),
                buttonsStyling: true,
              }).then(() => {
                let timestampParam = +new Date();
                if (this.isIndentList) {
                  this.$router.push('/produkty-indent/edycja/' + this.id + '?refresh=' + timestampParam);
                } else {
                  this.$router.push('/produkty/edycja/' + this.id + '?refresh=' + timestampParam);
                }
              });
            }
          });
        }
      });
    },
    addPrice () {
      this.prices.push({
        currency_id: null,
        price: ''
      });
    },
    removePrice (index) {
      this.prices.splice(index, 1);
    },
    setIndentProductType (type) {
      this.type = type;

      if (type === 'CAP') {
        this.weight = '0.08';
        this.cn_code = '65050030';
      } else if (type === 'WINTER') {
        this.weight = '0.09';
        this.cn_code = '65050090';
      }
    },
    reloadVariants () {
      this.loadedData = false;

      FormUtils.loadItemData(this, {
        method: 'get',
        endpointBase: '/products/item/',
        instanceFields: ['children'],
        loadedKey: 'loadedData',
        errorKey: 'loadError',
        params: {
          with: ['children', 'children.productPrices']
        }
      });
    },
    closeAddVariantPopup () {
      this.showAddVariantPopup = false;
    },
    allowPriceCodeEdit () {
      Vue.swal({
        title: this.$t('product.doYouWantEditPriceCode'),
        text: '',
        type: 'question',
        showCancelButton: true,
        confirmButtonText: this.$t('ui.yes'),
        cancelButtonText: this.$t('ui.cancel')
      }).then((result) => {
        if (result.value) {
          this.priceCodeEditAllowed = true;

          Vue.swal({
            title: this.$t('product.doYouWantEditPriceCodeInOtherProducts'),
            text: '',
            type: 'question',
            showCancelButton: true,
            confirmButtonText: this.$t('ui.yes'),
            cancelButtonText: this.$t('ui.cancel')
          }).then((answer) => {
            if (answer.value) {
              this.copyPriceCodeDuringSave = true;
            }
          });
        }
      });
    },
    closeSamePricePopup () {
      this.showSamePriceCodeProductsPopup = false;
    },
    downloadFile (fileURL) {
      if (this.downloadingFile) {
        return;
      }

      this.downloadingFile = true;

      this.$http.post(fileURL, {}, {
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        let filename = response.request.getResponseHeader('content-disposition').split('filename=')[1].split(';')[0];
        link.setAttribute('download', filename.replace(/"/gmi, ''));
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.downloadingFile = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.errorOccuredDuringDownload'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        this.downloadingFile = false;
      });
    },
  },
  beforeDestroy () {
    this.$bus.$off('product-translation-new-popup-close', this.closeAddTranslationPopup);
    this.$bus.$off('product-translation-edit-popup-close', this.closeEditTranslationPopup);
    this.$bus.$off('product-reload-translations', this.loadTranslations);
    this.$bus.$off('product-edit-variants-reload-needed', this.reloadVariants);
    this.$bus.$off('product-variant-new-popup-close', this.closeAddVariantPopup);
    this.$bus.$off('product-same-price-code-popup-close', this.closeSamePricePopup);
  }
};
</script>

<style lang="scss" scoped>
.is-draggable {
  cursor: move;
}

.additional-categories {
  list-style-type: none;
  padding-left: 0;

  .additional-category.is-disabled {
    opacity: 0.5;

    /deep/ .custom-toggle .custom-control-input ~ .custom-control-label::before {
      background: #17c671;
      border-color: #17c671;
    }

    /deep/ .custom-toggle.custom-toggle-sm .custom-control-input ~ .custom-control-label::after {
      background: #fff;
      left: 1.9375rem;
      transform: translateX(-100%);
    }
  }
}

.tags-category {
  ol {
    display: flex;
    flex-wrap: wrap;
    list-style-type: none;
    margin: 0;
    padding: 0;

    li {
      background: #ddd;
      border-radius: 5px;
      color: #222;
      cursor: pointer;
      margin-bottom: 10px;
      margin-right: 10px;
      padding: 5px 10px;

      &[data-active="true"] {
        background: var(--success);
        color: #fff;
        text-shadow: 0 2px 3px rgba(0, 0, 0, .25);
      }
    }
  }
}
</style>
